type SettingValue = string | boolean;

class LocalSettingsManager {
  private readonly PREFIX = "ccs";

  /**
   * Generates a clash-resistant localStorage key
   */
  private getStorageKey(module: string, key: string): string {
    return `${this.PREFIX}-${module}-${key}`;
  }

  /**
   * Loads a setting from localStorage with a default fallback
   * @param module The module name (e.g., 'dashboard', 'registers')
   * @param key The setting key (e.g., 'getStartedMinimized')
   * @param defaultValue The default value if setting doesn't exist
   * @returns The stored value or default
   */
  loadSetting<T extends SettingValue>(
    module: string,
    key: string,
    defaultValue: T,
  ): T {
    try {
      const storageKey = this.getStorageKey(module, key);
      const stored = localStorage.getItem(storageKey);

      if (stored === null) {
        return defaultValue;
      }
      try {
        const parsed = JSON.parse(stored);
        return parsed as T;
      } catch {
        return stored as T;
      }
    } catch (error) {
      console.warn(`Failed to load setting ${module}.${key}:`, error);
      return defaultValue;
    }
  }

  /**
   * Saves a setting to localStorage
   * @param module The module name (e.g., 'dashboard', 'registers')
   * @param key The setting key (e.g., 'getStartedMinimized')
   * @param value The value to store
   */
  saveSetting<T extends SettingValue>(
    module: string,
    key: string,
    value: T,
  ): void {
    try {
      const storageKey = this.getStorageKey(module, key);
      const serialized = JSON.stringify(value);
      localStorage.setItem(storageKey, serialized);
    } catch (error) {
      console.warn(`Failed to save setting ${module}.${key}:`, error);
    }
  }
}

export const LocalSettings = new LocalSettingsManager();
